import { ApparatDeviceBox, AudioFileBox, CubedDeviceBox, NeonDeviceBox, WclapInstrumentBox, TubularDeviceBox, MIDIOutputDeviceBox, NanoDeviceBox, InstrumentCompositeBox, PlayfieldDeviceBox, PlayfieldSampleBox, SoundfontDeviceBox, SoundfontFileBox, TapeDeviceBox, VaporisateurDeviceBox } from "@opendaw/studio-boxes";
import { isDefined, UUID } from "@opendaw/lib-std";
import { ClassicWaveform } from "@opendaw/lib-dsp";
import { IconSymbol, VoicingMode } from "@opendaw/studio-enums";
import { DeviceManualUrls } from "../DeviceManualUrls";
import { TrackType } from "../timeline/TrackType";
export var InstrumentFactories;
(function (InstrumentFactories) {
    InstrumentFactories.Tape = {
        defaultName: "Tape",
        defaultIcon: IconSymbol.Tape,
        briefDescription: "Audio Player",
        description: "Plays audio regions & clips",
        manualPage: DeviceManualUrls.Tape,
        trackType: TrackType.Audio,
        create: (boxGraph, host, name, icon, _attachment) => TapeDeviceBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            box.flutter.setValue(0.2);
            box.wow.setValue(0.05);
            box.noise.setValue(0.02);
            box.saturation.setValue(0.5);
            box.host.refer(host);
        })
    };
    InstrumentFactories.Nano = {
        defaultName: "Nano",
        defaultIcon: IconSymbol.NanoWave,
        briefDescription: "Polyphonic Sampler",
        description: "Polyphonic sampler with root key, region, crossfade loop and envelope",
        manualPage: DeviceManualUrls.Nano,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon, attachment) => NanoDeviceBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            if (isDefined(attachment)) {
                box.file.refer(attachment);
            }
            box.host.refer(host);
        })
    };
    InstrumentFactories.Playfield = {
        defaultName: "Playfield",
        defaultIcon: IconSymbol.Playfield,
        briefDescription: "Drum Machine",
        description: "Drum computer",
        manualPage: DeviceManualUrls.Playfield,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon, attachment) => {
            const deviceBox = PlayfieldDeviceBox.create(boxGraph, UUID.generate(), box => {
                box.label.setValue(name);
                box.icon.setValue(IconSymbol.toName(icon));
                box.host.refer(host);
            });
            if (isDefined(attachment)) {
                attachment.filter(({ note, uuid, name, durationInSeconds, exclude }) => {
                    const fileBox = useAudioFile(boxGraph, uuid, name, durationInSeconds);
                    PlayfieldSampleBox.create(boxGraph, UUID.generate(), box => {
                        box.device.refer(deviceBox.samples);
                        box.file.refer(fileBox);
                        box.index.setValue(note);
                        box.exclude.setValue(exclude);
                    });
                });
            }
            return deviceBox;
        }
    };
    InstrumentFactories.Wclap = {
        defaultName: "WebCLAP",
        defaultIcon: IconSymbol.WebClap,
        briefDescription: "WebCLAP Plugin",
        description: "Hosts a CLAP instrument compiled to WebAssembly (WebCLAP bundle)",
        manualPage: DeviceManualUrls.Wclap,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon, _attachment) => WclapInstrumentBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            box.host.refer(host);
        })
    };
    InstrumentFactories.Neon = {
        defaultName: "Neon",
        defaultIcon: IconSymbol.Neon,
        briefDescription: "CZ-style Synth",
        description: "CZ-style phase distortion synthesizer",
        manualPage: DeviceManualUrls.Neon,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon, _attachment) => NeonDeviceBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            // The init tone: line 1 saw, DCW fully open, organ-style DCA (full until note-off, short
            // release stage). Envelope array order: line1 pitch/DCW/DCA, line2 pitch/DCW/DCA.
            for (const line of [0, 1]) {
                const dcw = box.envelopes.fields()[line * 3 + 1];
                dcw.rate1.setInitValue(99);
                dcw.level1.setInitValue(99);
                dcw.sustain.setInitValue(1);
                dcw.end.setInitValue(2);
                dcw.rate2.setInitValue(99);
                const dca = box.envelopes.fields()[line * 3 + 2];
                dca.rate1.setInitValue(99);
                dca.level1.setInitValue(99);
                dca.sustain.setInitValue(1);
                dca.end.setInitValue(2);
                dca.rate2.setInitValue(70);
            }
            box.host.refer(host);
        })
    };
    InstrumentFactories.Cubed = {
        defaultName: "Cubed",
        defaultIcon: IconSymbol.Cube,
        briefDescription: "303-style Synth",
        description: "Acid bassline synthesizer",
        manualPage: "manuals/devices/instruments/cubed",
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon, _attachment) => CubedDeviceBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            box.host.refer(host);
        })
    };
    InstrumentFactories.Vaporisateur = {
        defaultName: "Vaporisateur",
        defaultIcon: IconSymbol.Vaporisateur,
        briefDescription: "Subtractive Synth",
        description: "Classic subtractive synthesizer",
        manualPage: DeviceManualUrls.Vaporisateur,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon, _attachment) => VaporisateurDeviceBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            box.cutoff.setInitValue(8000.0);
            box.resonance.setInitValue(0.1);
            box.attack.setInitValue(0.005);
            box.decay.setInitValue(0.100);
            box.sustain.setInitValue(0.5);
            box.release.setInitValue(0.5);
            box.voicingMode.setInitValue(VoicingMode.Polyphonic);
            box.lfo.rate.setInitValue(1.0);
            box.oscillators.fields()[0].waveform.setInitValue(ClassicWaveform.saw);
            box.oscillators.fields()[0].volume.setInitValue(-6.0);
            box.oscillators.fields()[1].volume.setInitValue(Number.NEGATIVE_INFINITY);
            box.oscillators.fields()[1].waveform.setInitValue(ClassicWaveform.square);
            box.host.refer(host);
            box.version.setValue(2); // for removing the -15db in voice and extended osc
        })
    };
    InstrumentFactories.MIDIOutput = {
        defaultName: "MIDIOutput",
        defaultIcon: IconSymbol.Midi,
        briefDescription: "Send MIDI",
        description: "MIDI Output",
        manualPage: DeviceManualUrls.MIDIOutput,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon, _attachment) => MIDIOutputDeviceBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            box.host.refer(host);
        })
    };
    InstrumentFactories.Soundfont = {
        defaultName: "Soundfont",
        defaultIcon: IconSymbol.SoundFont,
        briefDescription: "Soundfont Player",
        description: "Soundfont Player",
        manualPage: DeviceManualUrls.Soundfont,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon, attachment) => SoundfontDeviceBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            if (isDefined(attachment)) {
                box.file.refer(attachment);
            }
            box.host.refer(host);
        })
    };
    InstrumentFactories.Apparat = {
        defaultName: "Apparat",
        defaultIcon: IconSymbol.Code,
        briefDescription: "Scriptable Instrument",
        description: "User-scripted instrument",
        manualPage: DeviceManualUrls.Apparat,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon) => ApparatDeviceBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            box.host.refer(host);
        })
    };
    InstrumentFactories.InstrumentComposite = {
        defaultName: "Composite",
        defaultIcon: IconSymbol.Stack,
        briefDescription: "Layered instruments",
        description: "Plays several instruments at once, each layer with its own effects, volume and panning",
        manualPage: DeviceManualUrls.InstrumentComposite,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon) => {
            return InstrumentCompositeBox.create(boxGraph, UUID.generate(), box => {
                box.label.setValue(name);
                box.icon.setValue(IconSymbol.toName(icon));
                box.host.refer(host);
            });
        }
    };
    // MIDI Output is wired at unit level only
    InstrumentFactories.isLayerInstrument = (factory) => factory.trackType === TrackType.Notes && factory !== InstrumentFactories.MIDIOutput;
    InstrumentFactories.Tubular = {
        defaultName: "Tubular",
        defaultIcon: IconSymbol.Tubular,
        briefDescription: "6-op FM Synth",
        description: "Six-operator FM synthesizer, DX7 compatible",
        manualPage: DeviceManualUrls.Tubular,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon, _attachment) => TubularDeviceBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            // The INIT VOICE: OP1 is the only sounding carrier (operators are OP1..OP6 in panel order).
            box.operators.fields()[0].outputLevel.setInitValue(99);
            box.host.refer(host);
        })
    };
    InstrumentFactories.Named = { Apparat: InstrumentFactories.Apparat, Cubed: InstrumentFactories.Cubed, InstrumentComposite: InstrumentFactories.InstrumentComposite, Neon: InstrumentFactories.Neon, Tubular: InstrumentFactories.Tubular, MIDIOutput: InstrumentFactories.MIDIOutput, Nano: InstrumentFactories.Nano, Playfield: InstrumentFactories.Playfield, Soundfont: InstrumentFactories.Soundfont, Tape: InstrumentFactories.Tape, Vaporisateur: InstrumentFactories.Vaporisateur, Wclap: InstrumentFactories.Wclap };
    InstrumentFactories.keyOfBox = (box) => {
        const stripped = box.name.replace(/(Device|Instrument)?Box$/, "");
        return Object.hasOwn(InstrumentFactories.Named, stripped) ? stripped : undefined;
    };
    const useAudioFile = (boxGraph, fileUUID, name, duration) => boxGraph.findBox(fileUUID)
        .unwrapOrElse(() => AudioFileBox.create(boxGraph, fileUUID, box => {
        box.fileName.setValue(name);
        box.endInSeconds.setValue(duration);
    }));
    const useSoundfontFile = (boxGraph, fileUUID, name) => boxGraph.findBox(fileUUID)
        .unwrapOrElse(() => SoundfontFileBox.create(boxGraph, fileUUID, box => box.fileName.setValue(name)));
})(InstrumentFactories || (InstrumentFactories = {}));
